/**
 * RunnerPopupV2 Component
 * Floating card with runner quick stats (Pencil `yzpuu`)
 * Desktop: centered card overlay
 * Mobile: bottom sheet
 * Click through to Runner Detail page
 */

import { Link } from 'react-router-dom';
import type { RunnerSummary, RunnerDetail } from '../../lib/types';
import { formatTimeInZone } from '../../lib/timeFormatter';
import { RunnerAvatarV2 } from './RunnerAvatarV2';
import { StatusBadgeV2 } from './StatusBadgeV2';

interface RunnerPopupV2Props {
  runner: RunnerSummary;
  detail: RunnerDetail | null;
  raceSlug: string;
  raceTimeZone: string;
  onClose: () => void;
}

export function RunnerPopupV2({
  runner,
  detail,
  raceSlug,
  raceTimeZone,
  onClose,
}: RunnerPopupV2Props) {
  const isAnonymous = runner.is_anonymous;

  // Calculate elapsed time for finished runners
  let elapsedDisplay: string | null = null;
  if (detail && detail.split_times.length > 0 && runner.finish_time && detail.race_start_time) {
    const startMs = new Date(detail.race_start_time).getTime();
    const finishMs = new Date(runner.finish_time).getTime();
    const elapsed = finishMs - startMs;
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    elapsedDisplay = `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="v2-popup-overlay" onClick={onClose} />

      {/* Popup card */}
      <div className="v2-popup">
        {/* Header */}
        <div className="v2-popup-header">
          <RunnerAvatarV2
            firstName={isAnonymous ? '?' : runner.first_name}
            lastName={isAnonymous ? '?' : runner.last_name}
          />
          <div style={{ flex: 1 }}>
            <div className="v2-popup-name">
              {isAnonymous
                ? 'Anonymous Runner'
                : `${runner.first_name} ${runner.last_name}`}
            </div>
            <div className="v2-popup-meta">
              BIB {runner.bib}
              {runner.gender && ` · ${runner.gender}`}
            </div>
          </div>
          <StatusBadgeV2 status={runner.status} label={runner.status_short_label} />
        </div>

        {/* Stats grid */}
        <div className="v2-popup-stats">
          <div>
            <div className="v2-popup-stat-label">OVERALL RANK</div>
            <div className="v2-popup-stat-value">
              {runner.overall_place || '—'}
            </div>
          </div>
          <div>
            <div className="v2-popup-stat-label">GENDER RANK</div>
            <div className="v2-popup-stat-value">
              {runner.gender_place || '—'}
            </div>
          </div>
          <div>
            <div className="v2-popup-stat-label">LAST CHECKPOINT</div>
            <div className="v2-popup-stat-value" style={{ fontSize: 12 }}>
              {runner.last_checkin_station || '—'}
              {runner.last_checkin_time && (
                <span style={{ color: 'var(--v2-text-muted)', marginLeft: 4 }}>
                  {formatTimeInZone(runner.last_checkin_time, raceTimeZone)}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="v2-popup-stat-label">
              {runner.status === 'finished' ? 'FINISH TIME' : 'STATUS'}
            </div>
            <div className="v2-popup-stat-value" style={{ fontSize: 12 }}>
              {runner.status === 'finished' && elapsedDisplay
                ? elapsedDisplay
                : runner.status_label}
            </div>
          </div>
        </div>

        {/* View Detail link */}
        {!isAnonymous && (
          <Link
            to={`/v2/${raceSlug}/runner/${runner.id}`}
            className="v2-popup-link"
            onClick={onClose}
          >
            View Full Details
          </Link>
        )}
      </div>
    </>
  );
}
